// Upload Document modal - file upload with type selection, drag-drop, and expiry date
import React, { useState, useRef, useCallback } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from 'components/ui/dialog';
import { Upload, FileText, X, Loader2 } from 'lucide-react';
import documentsApi from 'api/documents';

const DOCUMENT_TYPES = [
  { value: 'passport', label: 'Passport' },
  { value: 'seamans_book', label: "Seaman's Book" },
  { value: 'medical_cert', label: 'Medical Certificate' },
  { value: 'training_cert', label: 'Training Certificate' },
  { value: 'contract', label: 'Contract' },
  { value: 'other', label: 'Other' },
];

const ALLOWED_TYPES = ['application/pdf', 'image/png', 'image/jpeg'];
const MAX_SIZE = 10 * 1024 * 1024; // 10MB

const UploadDocumentModal = ({ open, onClose, member, onUploadSuccess }) => {
  const [docType, setDocType] = useState('');
  const [file, setFile] = useState(null);
  const [expiryDate, setExpiryDate] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [dragActive, setDragActive] = useState(false);
  const fileInputRef = useRef(null);

  const resetForm = useCallback(() => {
    setDocType('');
    setFile(null);
    setExpiryDate('');
    setError('');
    setLoading(false);
    setDragActive(false);
  }, []);

  const handleClose = useCallback((val) => {
    resetForm();
    onClose(val);
  }, [onClose, resetForm]);

  const validateFile = (f) => {
    if (!ALLOWED_TYPES.includes(f.type)) {
      setError('Only PDF, PNG, and JPG files are allowed.');
      return false;
    }
    if (f.size > MAX_SIZE) {
      setError('File size must be under 10MB.');
      return false;
    }
    setError('');
    return true;
  };

  const handleFileSelect = (f) => {
    if (f && validateFile(f)) {
      setFile(f);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragActive(false);
    const droppedFile = e.dataTransfer?.files?.[0];
    if (droppedFile) handleFileSelect(droppedFile);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setDragActive(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setDragActive(false);
  };

  const handleSubmit = async () => {
    if (!file || !docType) {
      setError('Please select a document type and file.');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('employeeId', member._id);
      formData.append('clientId', member.clientId);
      formData.append('name', DOCUMENT_TYPES.find((t) => t.value === docType)?.label || docType);
      formData.append('type', docType);
      if (expiryDate) formData.append('expiryDate', expiryDate);

      await documentsApi.upload(formData);
      handleClose(false);
      if (onUploadSuccess) onUploadSuccess();
    } catch (err) {
      setError(err?.response?.data?.message || 'Upload failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (!member) return null;

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:tw-max-w-md">
        <DialogHeader>
          <DialogTitle className="tw-flex tw-items-center tw-gap-2">
            <Upload className="tw-h-5 tw-w-5" />
            Upload Document
          </DialogTitle>
          <DialogDescription>
            Upload a document for {member?.firstName} {member?.lastName}.
          </DialogDescription>
        </DialogHeader>

        <div className="tw-space-y-4 tw-py-4">
          {/* Document Type */}
          <div>
            <label className="tw-text-sm tw-font-medium tw-text-foreground tw-block tw-mb-1.5">
              Document Type
            </label>
            <select
              value={docType}
              onChange={(e) => setDocType(e.target.value)}
              className="tw-w-full tw-h-9 tw-rounded-md tw-border tw-border-input tw-bg-background tw-pl-3 tw-pr-8 tw-text-sm"
            >
              <option value="">Select document type...</option>
              {DOCUMENT_TYPES.map((t) => (
                <option key={t.value} value={t.value}>{t.label}</option>
              ))}
            </select>
          </div>

          {/* File Drop Zone */}
          <div
            onDrop={handleDrop}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onClick={() => fileInputRef.current?.click()}
            className={`tw-border-2 tw-border-dashed tw-rounded-lg tw-p-8 tw-text-center tw-cursor-pointer tw-transition-colors ${
              dragActive
                ? 'tw-border-indigo-500 tw-bg-indigo-50'
                : file
                ? 'tw-border-green-300 tw-bg-green-50'
                : 'tw-border-gray-300 hover:tw-border-gray-400'
            }`}
          >
            <input
              ref={fileInputRef}
              type="file"
              accept=".pdf,.png,.jpg,.jpeg"
              className="tw-hidden"
              onChange={(e) => handleFileSelect(e.target.files?.[0])}
            />
            {file ? (
              <div className="tw-flex tw-items-center tw-justify-center tw-gap-3">
                <FileText className="tw-h-6 tw-w-6 tw-text-green-600" />
                <div className="tw-text-left">
                  <p className="tw-text-sm tw-font-medium tw-text-gray-900 tw-m-0">{file.name}</p>
                  <p className="tw-text-xs tw-text-gray-500 tw-m-0">{formatFileSize(file.size)}</p>
                </div>
                <button
                  onClick={(e) => { e.stopPropagation(); setFile(null); }}
                  className="tw-p-1 tw-text-gray-400 hover:tw-text-gray-600 tw-bg-transparent tw-border-0 tw-cursor-pointer"
                >
                  <X className="tw-h-4 tw-w-4" />
                </button>
              </div>
            ) : (
              <>
                <FileText className="tw-h-8 tw-w-8 tw-text-muted-foreground tw-mx-auto tw-mb-2" />
                <p className="tw-text-sm tw-text-muted-foreground tw-m-0">
                  Click to upload or drag and drop
                </p>
                <p className="tw-text-xs tw-text-muted-foreground tw-mt-1 tw-mb-0">
                  PDF, PNG, or JPG (max. 10MB)
                </p>
              </>
            )}
          </div>

          {/* Expiry Date */}
          <div>
            <label className="tw-text-sm tw-font-medium tw-text-foreground tw-block tw-mb-1.5">
              Expiry Date (Optional)
            </label>
            <input
              type="date"
              value={expiryDate}
              onChange={(e) => setExpiryDate(e.target.value)}
              className="tw-w-full tw-h-9 tw-rounded-md tw-border tw-border-input tw-bg-background tw-px-3 tw-text-sm"
            />
          </div>

          {/* Error */}
          {error && (
            <p className="tw-text-sm tw-text-red-600 tw-m-0">{error}</p>
          )}
        </div>

        <DialogFooter>
          <button
            onClick={() => handleClose(false)}
            className="tw-h-9 tw-px-4 tw-rounded-md tw-border tw-border-input tw-bg-background tw-text-sm tw-font-medium hover:tw-bg-accent tw-cursor-pointer"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={loading || !file || !docType}
            className={`tw-h-9 tw-px-4 tw-rounded-md tw-bg-indigo-600 tw-text-white tw-text-sm tw-font-medium tw-border-0 tw-cursor-pointer tw-flex tw-items-center tw-gap-2 ${
              loading || !file || !docType ? 'tw-opacity-50 tw-cursor-not-allowed' : 'hover:tw-bg-indigo-700'
            }`}
          >
            {loading ? <Loader2 className="tw-h-4 tw-w-4 tw-animate-spin" /> : <Upload className="tw-h-4 tw-w-4" />}
            {loading ? 'Uploading...' : 'Upload File'}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default UploadDocumentModal;
